import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Gift, Undo2, RefreshCw, Trash2, UserX } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual + copy mapping for every action the audit log knows about.
// Kept in sync with routers/admin.py — grant, revoke, delete_practice_data, delete_account.
const ACTION_META = {
  grant: {
    icon: Gift, bg: "#E7F0F4", fg: "#3090A8",
    label: "Granted", verb: "waiver for",
  },
  revoke: {
    icon: Undo2, bg: "#FEF3E7", fg: "#B07B3A",
    label: "Revoked", verb: "waiver for",
  },
  delete_practice_data: {
    icon: Trash2, bg: "#F1ECE3", fg: "#7A6A4F",
    label: "Cleared", verb: "practice data for",
  },
  delete_account: {
    icon: UserX, bg: "#FEF2F0", fg: "#D98880",
    label: "Deleted", verb: "account",
  },
  unknown: {
    icon: Clock, bg: "#EAEAEA", fg: "#4A5568",
    label: "Action", verb: "on",
  },
};

// Admin-only timeline of waiver grants + revokes. Reads from
// GET /api/admin/waiver-audit. Each row shows admin, target, action,
// expiry (or "permanent"), and a relative timestamp.

const fmtRelative = (iso) => {
  if (!iso) return "";
  const delta = (Date.now() - new Date(iso).getTime()) / 1000;
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)} min ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)} hr ago`;
  if (delta < 604800) return `${Math.floor(delta / 86400)} d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
};

const fmtDate = (iso) => {
  if (!iso) return "permanent";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
};

export const WaiverAuditTimeline = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/waiver-audit?limit=50`, {
        withCredentials: true,
      });
      setRows(res.data || []);
    } catch (e) {
      toast.error("Could not load waiver audit log.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div
      className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-8"
      data-testid="waiver-audit-timeline"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-outfit font-medium text-lg text-[#1B2349] flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#3090A8]" />
            Waiver Audit Trail
          </h2>
          <p className="text-sm text-[#718096]">
          Grants, revokes, and admin data deletions, newest first.
          </p>
        </div>
        <button
          onClick={refresh}
          className="text-xs text-[#3090A8] hover:text-[#1B2349] flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-slate-50"
          data-testid="waiver-audit-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="py-8 text-center text-[#718096] text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-[#718096] text-sm">
          No waiver activity yet.
        </div>
      ) : (
        <ol className="space-y-3">
          {rows.map((r, idx) => {
            const meta = ACTION_META[r.action] || ACTION_META.unknown;
            const Icon = meta.icon;
            const appliedSummary =
              (r.action === "delete_practice_data" || r.action === "delete_account") &&
              r.applied && typeof r.applied === "object"
                ? Object.entries(r.applied)
                    .filter(([, n]) => Number(n) > 0)
                    .map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`)
                    .join(", ")
                : null;
            return (
              <li
                key={`${r.timestamp}-${idx}`}
                className="flex items-start gap-3 pb-3 border-b border-slate-50 last:border-b-0"
                data-testid="waiver-audit-row"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: meta.bg }}
                >
                  <Icon className="w-4 h-4" style={{ color: meta.fg }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                    <span className="font-medium text-[#1B2349]">
                      {meta.label}
                    </span>
                    <span className="text-[#4A5568]">
                      {meta.verb}
                      <span className="font-medium text-[#1B2349] ml-1">
                        {r.target_email || r.target_user_id}
                      </span>
                    </span>
                    {r.action === "grant" && (
                      <span className="text-xs text-[#718096]">
                        · expires {fmtDate(r.waived_until)}
                      </span>
                    )}
                  </div>
                  {appliedSummary && (
                    <div className="text-xs text-[#718096] mt-0.5">
                      Wiped: {appliedSummary}
                    </div>
                  )}
                  <div className="text-xs text-[#718096] mt-0.5">
                    by {r.admin_email || r.admin_user_id} · {fmtRelative(r.timestamp)}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default WaiverAuditTimeline;
